"use client";

import {
  ReactNode,
  ElementType,
  cloneElement,
  isValidElement,
  forwardRef
} from "react";
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton
} from "react-share";
import { FaXTwitter } from "react-icons/fa6";
import { FiFacebook, FiLinkedin, FiMail } from "react-icons/fi";
import { useTranslator } from "@eo-locale/react";
import dynamic from "next/dynamic";

import {
  Popover as ChakraPopover,
  Portal,
  Box as ChakraBox,
  type HTMLChakraProps
} from "@chakra-ui/react";

import { HStack, Box, Icon } from "../internal/exports";
import { asSlotComponent } from "../internal/v3-slot";

const PopoverTriggerSlot = asSlotComponent(ChakraPopover.Trigger);
const PopoverPositioner = asSlotComponent(ChakraPopover.Positioner);
const PopoverContentSlot = asSlotComponent(ChakraPopover.Content);
const PopoverBodySlot = asSlotComponent(ChakraPopover.Body);

interface SocialShareProps {
  title: string;
  shareUrl: string;
  trigger: ReactNode;
  placement?: string;
}

const placementAliases: Record<string, string> = {
  auto: "bottom",
  "auto-start": "bottom-start",
  "auto-end": "bottom-end"
};

function resolvePlacement(placement?: string) {
  const value = placement || "auto-end";
  return placementAliases[value] ?? value;
}

type TriggerWithBoxProps = { trigger: ReactNode } & Record<string, unknown>;

const TriggerWithBox = forwardRef<HTMLDivElement, TriggerWithBoxProps>(
  ({ trigger, ...triggerProps }, ref) => (
    <ChakraBox ref={ref} display="inline-block">
      {isValidElement(trigger)
        ? cloneElement(trigger, {
            ...triggerProps,
            "aria-haspopup": "dialog"
          } as Record<string, unknown>)
        : (trigger as ReactNode)}
    </ChakraBox>
  )
);
TriggerWithBox.displayName = "TriggerWithBox";

function SocialShare(props: SocialShareProps) {
  const { translate } = useTranslator();
  const { placement, shareUrl, trigger, title } = props;

  const ShareIcon = (
    props: HTMLChakraProps<"svg"> & {
      icon: ElementType;
      colorPalette: string;
    }
  ) => {
    const { colorPalette, icon, ...rest } = props;

    return (
      <Box
        as="span"
        boxSize={8}
        display="flex"
        borderRadius="full"
        alignItems="center"
        justifyContent="center"
        colorPalette={colorPalette}
        bg="colorPalette.500"
        _hover={{ bg: "colorPalette.600" }}
        aria-hidden="true"
      >
        <Icon as={icon} boxSize={4} color="white" aria-hidden {...rest} />
      </Box>
    );
  };

  return (
    <ChakraPopover.Root
      positioning={{
        placement: resolvePlacement(placement),
        gutter: 8,
        flip: true
      }}
    >
      <PopoverTriggerSlot>
        <TriggerWithBox trigger={trigger} />
      </PopoverTriggerSlot>
      <Portal>
        <PopoverPositioner>
          <PopoverContentSlot
            width="auto"
            borderRadius="lg"
            borderColor="gray.200"
            aria-label={translate("common.socialMedia.shareOptions")}
          >
            <PopoverBodySlot
              as={HStack}
              gap={4}
              py={{ xl: "0.8rem", base: 4 }}
              px={{ xl: 4, base: "0.8rem" }}
              css={{
                button: {
                  height: 8,
                  outline: "none",
                  borderRadius: "full",
                  _focusVisible: { boxShadow: "outline" }
                }
              }}
            >
              <FacebookShareButton
                title={title}
                url={shareUrl}
                aria-label={translate("common.socialMedia.shareToFacebook")}
              >
                <ShareIcon icon={FiFacebook} colorPalette="facebook" />
              </FacebookShareButton>
              <TwitterShareButton
                title={title}
                url={shareUrl}
                aria-label={translate("common.socialMedia.shareToX")}
              >
                <ShareIcon icon={FaXTwitter} colorPalette="x" />
              </TwitterShareButton>
              <LinkedinShareButton
                title={title}
                url={shareUrl}
                aria-label={translate("common.socialMedia.shareToLinkedIn")}
              >
                <ShareIcon icon={FiLinkedin} colorPalette="linkedin" />
              </LinkedinShareButton>
              <EmailShareButton
                body={title}
                separator={"\n"}
                url={shareUrl}
                aria-label={translate("common.socialMedia.shareToEmail")}
              >
                <ShareIcon icon={FiMail} colorPalette="brand" />
              </EmailShareButton>
            </PopoverBodySlot>
          </PopoverContentSlot>
        </PopoverPositioner>
      </Portal>
    </ChakraPopover.Root>
  );
}

const SocialShareWithNoSSR = dynamic(() => Promise.resolve(SocialShare), {
  ssr: false
});

export { SocialShareWithNoSSR as SocialShare };
export type { SocialShareProps };
